'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import {
	Paper,
	Button,
} from '@mui/material';
import { Tabs, Spinner } from '@repo/components';
import { guardedClient as guardedRequest } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import {
	Settings,
	ApiCredentialsSettings,
	MailServerSettings,
	MicrosoftGraphSettings,
	MonitoringSettings,
} from '@repo/shared/domain';
import { getMultipleSettings } from 'src/api/admin/settings';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
import { useConfig } from 'src/hooks/useConfig';
import MonitoringTab from './Tabs/MonitoringTab';
import APITab from './Tabs/APITab';
import NotificationsTab from './Tabs/NotificationsTab';
import MicrosoftGraphTab from './Tabs/MicrosoftGraphTab';
import WebhooksTab, { type WebhooksData } from './Tabs/WebhooksTab';
import SSOTab, { type SSOSettings, defaultSSOSettings } from './Tabs/SSOTab';

export const Route = createFileRoute('/_admin/config/integrations/')({
	component: IntegrationsPage,
});

function IntegrationsPage() {
	const queryClient = useQueryClient();
	const { config } = useConfig();
	const webhooksEnabled = config?.featureFlags?.USES_WEBHOOKS || false;
	const tenantUrl = typeof window !== 'undefined' ? window.location.origin : '';

	const [hasChanged, setHasChanged] = useState(false);
	const [ssoData, setSSOData] = useState<SSOSettings>({ ...defaultSSOSettings });
	const [mailServerData, setMailServerData] = useState<MailServerSettings['settings']>({});
	const [microsoftGraphData, setMicrosoftGraphData] = useState<MicrosoftGraphSettings['settings']>({});
	const [monitoringData, setMonitoringData] = useState<MonitoringSettings['settings']>({});
	const [activeTab, setActiveTab] = useState(0);
	const [initialized, setInitialized] = useState(false);

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Integrations');

	const { data: settingsData, isLoading: settingsLoading } = useQuery({
		queryKey: ['integrations-settings'],
		queryFn: async () => {
			const results = await getMultipleSettings([
				Settings.SSO,
				Settings.ApiCredentials,
				Settings.MailServer,
				Settings.MicrosoftGraph,
				Settings.Monitoring,
			]);
			return results;
		},
	});

	const { data: rolesData, isLoading: rolesLoading } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: () => getRolesForAutocomplete(),
	});

	const { data: webhooksData } = useQuery({
		queryKey: ['webhooks'],
		queryFn: async () => {
			try {
				const { body } = await guardedRequest.get<undefined, WebhooksData>('/api/settings/webhooks', { schema: undefined });
				return body || { data: [], count: 0 };
			} catch {
				return { data: [], count: 0 };
			}
		},
		enabled: webhooksEnabled,
	});

	if (settingsData && !initialized) {
		const ssoSettings = (settingsData[Settings.SSO] as any)?.settings || {};
		const mailServerSettings = (settingsData[Settings.MailServer] as any)?.settings || {};
		const microsoftGraphSettings = (settingsData[Settings.MicrosoftGraph] as any)?.settings || {};
		const monitoringSettings = (settingsData[Settings.Monitoring] as any)?.settings || {};
		setSSOData({ ...defaultSSOSettings, ...ssoSettings });
		const method = mailServerSettings?.method || (mailServerSettings?.smtp ? 'smtp' : 'api');
		setMailServerData({ ...mailServerSettings, method });
		setMicrosoftGraphData(microsoftGraphSettings);
		setMonitoringData(monitoringSettings);
		setInitialized(true);
	}

	const roles = rolesData || [];
	const apiSettings = settingsData?.[Settings.ApiCredentials] as ApiCredentialsSettings | undefined;
	const webhooks = webhooksData || { data: [], count: 0 };
	const originalSSOData = settingsData?.[Settings.SSO]
		? { ...defaultSSOSettings, ...(settingsData[Settings.SSO] as any)?.settings }
		: { ...defaultSSOSettings };

	const ssoOnChange = useCallback((data: SSOSettings) => {
		setSSOData(data);
		setHasChanged(true);
	}, []);

	const mailServerOnChange = useCallback((data: MailServerSettings['settings']) => {
		setMailServerData(data);
		setHasChanged(true);
	}, []);

	const microsoftGraphOnChange = useCallback((data: MicrosoftGraphSettings['settings']) => {
		setMicrosoftGraphData(data);
		setHasChanged(true);
	}, []);

	const monitoringOnChange = useCallback((data: MonitoringSettings['settings']) => {
		setMonitoringData(data);
		setHasChanged(true);
	}, []);

	const saveChanges = async () => {
		const tabs = getTabs();
		const currentTab = tabs[activeTab];
		if (!currentTab) return;

		try {
			if (currentTab.label === 'SAML SSO') {
				await guardedRequest.put('/api/settings/auth-sso', ssoData);
			} else if (currentTab.label === 'Monitoring') {
				await guardedRequest.put('/api/settings/monitoring', monitoringData);
			} else if (currentTab.label === 'Notifications') {
				const cleanedData: Record<string, unknown> = { ...mailServerData };
				const method = (cleanedData as any).method;

				if (method !== 'smtp') {
					delete cleanedData.smtp;
					delete cleanedData.host;
					delete cleanedData.port;
					delete cleanedData.auth;
				}

				if (method === 'smtp') {
					cleanedData.smtp = true;
				}

				// Graph credentials are saved separately via the Microsoft Graph tab. If any
				// stale Graph fields linger on the in-memory mail-server doc (e.g., pre-migration
				// data), strip them before saving so they don't get re-persisted.
				delete cleanedData.tenantId;
				delete cleanedData.clientId;
				delete cleanedData.clientSecret;
				delete cleanedData.senderEmail;

				await guardedRequest.put('/api/settings/mail-server', cleanedData);
			} else if (currentTab.label === 'Microsoft Graph') {
				await guardedRequest.put('/api/settings/ms-graph', microsoftGraphData);
			}
			setHasChanged(false);
			queryClient.invalidateQueries({ queryKey: ['integrations-settings'] });
			EventEmitter.emit('toast', { type: 'success', message: 'Settings saved successfully' });
		} catch (error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to save settings' });
		}
	};

	const getTabs = () => [
		{
			label: 'Monitoring',
			el: <MonitoringTab data={monitoringData} onChange={monitoringOnChange} />,
		},
		{
			label: 'SAML SSO',
			el: <SSOTab onChange={ssoOnChange} originalSSOData={originalSSOData} roles={roles} tenantUrl={tenantUrl} />,
		},
		{
			label: 'Notifications',
			el: <NotificationsTab data={mailServerData} onChange={mailServerOnChange} />,
		},
		{
			label: 'Microsoft Graph',
			el: <MicrosoftGraphTab data={microsoftGraphData} onChange={microsoftGraphOnChange} />,
		},
		{
			label: 'API',
			el: <APITab apiSettings={apiSettings} />,
		},
		...(webhooksEnabled
			? [
					{
						label: 'Webhooks',
						el: <WebhooksTab webhooks={webhooks} />,
					},
				]
			: []),
	];

	const isLoading = settingsLoading || rolesLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const tabs = getTabs();

	return (
		<div className="flex flex-col p-4">
			<div className="flex mb-3">
				<div className="flex-grow" />
				<Button disabled={!hasChanged} variant="outlined" onClick={saveChanges}>
					Save Changes
				</Button>
			</div>
			<Paper variant="outlined">
				<Tabs value={activeTab} onChange={(value) => setActiveTab(value)}>
					<Tabs.TabList>
						{tabs.map((t, index) => (
							<Tabs.Tab key={t.label} value={index} label={t.label} />
						))}
					</Tabs.TabList>
					{tabs.map((t, index) => (
						<Tabs.TabPanel key={t.label} value={index} selectedValue={activeTab}>
							<div>{t.el}</div>
						</Tabs.TabPanel>
					))}
				</Tabs>
			</Paper>
		</div>
	);
}
